import type { IdentityUser } from '../providers';

/**
 * Set by the post-login Action on the login that follows an Auth0 signup
 * (`registration_claim_pending`, or first login with `migrated_from: 'registration'`).
 * The pending marker survives an abandoned onboarding or verification prompt
 * and is cleared after the flow completes. The claim is omitted for ordinary logins.
 */
export const AUTH0_UUID_CLAIM = 'petsmart.com/uuid' as const;
export const AUTH0_REGISTRATION_CLAIM = 'petsmart.com/registration' as const;
export const AUTH0_LOYALTY_ID_CLAIM = 'petsmart.com/loyalty_id' as const;
export const AUTH0_EMAIL_CLAIM = 'petsmart.com/email' as const;
export const AUTH0_FAMILY_NAME_CLAIM = 'petsmart.com/family_name' as const;
export const AUTH0_GIVEN_NAME_CLAIM = 'petsmart.com/given_name' as const;

export type Auth0IdTokenClaims = {
    [key: string]: unknown;
    'petsmart.com/acr'?: string;
    'petsmart.com/cusomter_key'?: string;
    [AUTH0_REGISTRATION_CLAIM]?: boolean;
    [AUTH0_LOYALTY_ID_CLAIM]?: string;
    [AUTH0_UUID_CLAIM]?: string;
    aud?: string | string[];
    email?: string;
    email_verified?: boolean;
    exp?: number;
    family_name?: string;
    given_name?: string;
    iss?: string;
    sid?: string;
    sub?: string;

    // Present on AOS sessions only
    is_aos_session?: boolean;
    [AUTH0_EMAIL_CLAIM]?: string;
    [AUTH0_FAMILY_NAME_CLAIM]?: string;
    [AUTH0_GIVEN_NAME_CLAIM]?: string;
};

function parseNumberClaim(value: unknown) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsedValue = Number(value);
        if (Number.isFinite(parsedValue)) {
            return parsedValue;
        }
    }

    return 0;
}

function parseStringClaim(value: unknown) {
    return typeof value === 'string' ? value : '';
}

export type CombinedUserClaims = Auth0IdTokenClaims & IdentityUser;

export function isAuth0AosSession(claims: Auth0IdTokenClaims | undefined): boolean {
    return claims?.is_aos_session === true;
}

/**
 * True when the session carries a usable Treats Rewards membership id.
 *
 * The Auth0 post-login Action stamps `'pending'` when `app_metadata.loyalty_id` is not set yet,
 * and enrollment can complete minutes after the token was issued, so every consumer has to treat
 * `'pending'`, `0` and an absent claim the same way. Accepts raw id-token claims or the mapped
 * user. The namespaced claim wins when present: it matches what the access token sends to
 * loyalty APIs, while the mapped user's legacy `loyaltyid` can be overwritten by the profile
 * response. The legacy claim is only used for sessions that never carried the namespaced one.
 */
export function hasAuth0LoyaltyId(claims: Maybe<Auth0IdTokenClaims | CombinedUserClaims>): boolean {
    return Boolean(Number(claims?.[AUTH0_LOYALTY_ID_CLAIM] ?? claims?.loyaltyid));
}

/**
 * Maps Auth0 `id_token` claims onto the legacy {@link IdentityUser} shape so the
 * rest of the app can consume an Auth0 session through the same contract as the
 * cookie-JWT flow. Only used when `ff_auth0` is on.
 *
 * Assumes the Auth0 dashboard Action injects the legacy claims (`customerprofileid`,
 * `loyaltyid`, `janrainaccesstoken`, `sourcelocation`, …) as flat, non-namespaced
 * keys. As a safety net every raw claim is preserved on the returned object, and
 * the legacy fields are guaranteed on top so existing consumers keep working even
 * if a claim is missing (falls back to `0` / `''`).
 */
export function mapAuth0IdTokenToIdentityUser(claims: Auth0IdTokenClaims): NonNullable<CombinedUserClaims> {
    const audience = claims.aud;
    const aud = parseStringClaim(Array.isArray(audience) ? audience[0] : audience);
    const exp = typeof claims.exp === 'number' && Number.isFinite(claims.exp) ? claims.exp : 0;

    return {
        ...claims,
        aud,
        customerprofileid: parseNumberClaim(claims.customerprofileid || claims['petsmart.com/customer_key']),
        elevatedauthexpiresat: parseNumberClaim(claims.elevatedauthexpiresat),
        email: parseStringClaim(claims.email || claims[AUTH0_EMAIL_CLAIM]),
        exp,
        gn:
            parseStringClaim(claims.gn) ||
            parseStringClaim(claims.given_name) ||
            parseStringClaim(claims[AUTH0_GIVEN_NAME_CLAIM]),
        iss: parseStringClaim(claims.iss),
        janrainaccesstoken: parseStringClaim(claims.janrainaccesstoken),
        loyaltyid: parseNumberClaim(claims.loyaltyid || claims[AUTH0_LOYALTY_ID_CLAIM]),
        rol: Array.isArray(claims.rol) ? claims.rol : [],
        sid: parseStringClaim(claims.sid || claims.sub),
        sn: parseStringClaim(claims.sn || claims.family_name || claims[AUTH0_FAMILY_NAME_CLAIM]),
        sourcelocation: parseNumberClaim(claims.sourcelocation),
    };
}
